// Browser-only adapter for Windows Hello / platform-authenticator unlock.
//
// It produces a 256-bit "hardware secret" (a WebAuthn PRF output) that the
// authenticator releases only after a successful user verification — a Windows
// Hello face/fingerprint check or the device PIN. packages/core then uses that
// secret to wrap the device secret, so a copy of local storage cannot be used
// to brute-force the app PIN offline.
//
// None of this can run in Node tests (there is no authenticator), so verify it
// live in Edge/Chrome on a device that has Windows Hello set up.
import { decodeBytes, encodeBytes } from '@safewallet/core';

const RP_NAME = 'Safe Wallet';
const PRF_SALT_BYTES = 32;
const WEBAUTHN_TIMEOUT_MS = 60_000;

// The DOM lib does not always type the PRF extension; describe just the shape we read.
type PrfGetResults = { prf?: { results?: { first?: ArrayBuffer } } };

export interface HelloEnrollment {
  credentialId: string;
  prfSalt: string;
  /** 256-bit secret for wrapping the device secret; the caller must clear it after use. */
  hardwareSecret: Uint8Array;
}

function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

// WebAuthn requires ArrayBuffer-backed views; copy so a SharedArrayBuffer-backed
// Uint8Array (from decoding) is never passed through.
function toBufferSource(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function friendlyWebAuthnError(cause: unknown): Error {
  if (cause instanceof DOMException && (cause.name === 'NotAllowedError' || cause.name === 'AbortError')) {
    return new Error('Cancelled.');
  }
  return cause instanceof Error ? cause : new Error('Not available right now.');
}

/** True only when this browser exposes a usable platform authenticator (e.g. Windows Hello). */
export async function helloSupported(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof PublicKeyCredential === 'undefined') return false;
  if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Register this device with Windows Hello and return the hardware secret plus the
 * identifiers we must store to reproduce it later. Throws if the browser can do
 * Windows Hello but not the PRF extension we rely on, so the caller can fall back
 * to PIN-only.
 */
export async function enrollHello(label: string): Promise<HelloEnrollment> {
  if (!(await helloSupported())) throw new Error('This device can’t do that.');
  const prfSalt = randomBytes(PRF_SALT_BYTES);
  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: {
        rp: { name: RP_NAME },
        user: { id: randomBytes(16), name: label || RP_NAME, displayName: label || RP_NAME },
        challenge: randomBytes(32),
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 },
          { type: 'public-key', alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          // Let the platform pick. Android only exposes the PRF hardware key through a
          // credential manager (Google Password Manager / Samsung Pass), so forcing a
          // non-discoverable credential silently disables fingerprint unlock on phones.
          // The cost is that on Android the key syncs with that account rather than
          // staying on one device; Windows generally still keeps it on the device.
          residentKey: 'preferred',
        },
        timeout: WEBAUTHN_TIMEOUT_MS,
        attestation: 'none',
        extensions: { prf: { eval: { first: prfSalt } } } as unknown as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
  } catch (cause) {
    throw friendlyWebAuthnError(cause);
  }
  if (!credential) throw new Error('Cancelled.');

  const credentialId = encodeBytes(new Uint8Array(credential.rawId));
  const prfSaltString = encodeBytes(prfSalt);

  // Always obtain the key with a read, never from the creation result. A read is the exact
  // path unlocking will use, so proving it works here is what makes it safe to delete the
  // plaintext device secret afterwards — turning this on can then never strand anyone.
  try {
    const hardwareSecret = await getHelloSecret(credentialId, prfSaltString);
    return { credentialId, prfSalt: prfSaltString, hardwareSecret };
  } catch (cause) {
    if (cause instanceof Error && cause.message === 'Cancelled.') throw cause;
    throw new Error('This device can’t do that. Your PIN keeps protecting your wallet.');
  }
}

/** Prompt Windows Hello and return the same hardware secret produced at enrollment. */
export async function getHelloSecret(credentialId: string, prfSalt: string): Promise<Uint8Array> {
  const idBytes = toBufferSource(await decodeBytes(credentialId));
  const saltBytes = toBufferSource(await decodeBytes(prfSalt));
  let assertion: PublicKeyCredential | null;
  try {
    assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32),
        allowCredentials: [{ type: 'public-key', id: idBytes }],
        userVerification: 'required',
        timeout: WEBAUTHN_TIMEOUT_MS,
        extensions: { prf: { eval: { first: saltBytes } } } as unknown as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
  } catch (cause) {
    throw friendlyWebAuthnError(cause);
  }
  if (!assertion) throw new Error('Cancelled.');
  const results = assertion.getClientExtensionResults() as unknown as PrfGetResults;
  const first = results.prf?.results?.first;
  if (!first) throw new Error('That didn’t work. Use your PIN.');
  return new Uint8Array(first);
}
